/**
 * Testimonials Tabbed - Custom Element
 *
 * Handles:
 * 1. Tab switching between Video Reviews and Written Reviews
 * 2. Video tab: "See More" progressive reveal (batch-based)
 * 3. Written tab: responsive Swiper (slider only when cards overflow viewport)
 */

if (!customElements.get('testimonials-tabbed')) {
  class TestimonialsTabbed extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.panels = [];
      this.seeMoreButtons = [];
      this.reviewsPerPage = 4;
      this.desktopColumns = 4;
      this.rowsPerBatch = 1;
      this.writtenSwiper = null;
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.reviewsPerPage = parseInt(this.dataset.reviewsPerPage, 10) || 4;
      this.rowsPerBatch = Math.max(1, Math.round(this.reviewsPerPage / this.desktopColumns));

      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.seeMoreButtons = Array.from(this.querySelectorAll('[data-see-more]'));

      this.bindTabEvents();
      this.bindSeeMoreEvents();
      this.bindVideoEvents();
      this.initializeResponsiveCardVisibility();
      this.initWrittenSlider();
      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.destroyWrittenSlider();
    }

    /* ------ Tab switching ------ */

    bindTabEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick.bind(this));
        tab.addEventListener('keydown', this.handleTabKeydown.bind(this));
      });
    }

    handleTabClick(event) {
      const clickedTab = event.currentTarget;
      this.activateTab(clickedTab);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      let newIndex;

      switch (event.key) {
        case 'ArrowLeft':
          newIndex = currentIndex - 1;
          if (newIndex < 0) newIndex = this.tabs.length - 1;
          break;
        case 'ArrowRight':
          newIndex = currentIndex + 1;
          if (newIndex >= this.tabs.length) newIndex = 0;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.tabs[newIndex].focus();
      this.activateTab(this.tabs[newIndex]);
    }

    activateTab(selectedTab) {
      const targetPanel = selectedTab.dataset.tab;

      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.classList.toggle('kizen-tr__tab--active', isSelected);
        tab.tabIndex = isSelected ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isTarget = panel.dataset.panel === targetPanel;
        panel.classList.toggle('kizen-tr__panel--active', isTarget);
        if (isTarget) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (targetPanel === 'written') {
        this.refreshWrittenSlider();
      }
    }

    /* ------ Video inline playback ------ */

    bindVideoEvents() {
      this.querySelectorAll('[data-play-video]').forEach((btn) => {
        btn.addEventListener('click', this.handleVideoToggle.bind(this));
      });
      this.querySelectorAll('[data-mute-toggle]').forEach((btn) => {
        btn.addEventListener('click', this.handleMuteToggle.bind(this));
      });
    }

    handleVideoToggle(event) {
      const btn = event.currentTarget;
      const wrap = btn.closest('.kizen-tr__media-wrap');
      if (!wrap) return;

      const video = wrap.querySelector('.kizen-tr__video');
      if (!video) return;

      const playIcon = btn.querySelector('.kizen-tr__icon-play');
      const pauseIcon = btn.querySelector('.kizen-tr__icon-pause');
      const muteBtn = wrap.querySelector('[data-mute-toggle]');

      if (video.paused) {
        video.removeAttribute('hidden');
        video.loop = true;
        video.play();
        if (playIcon) playIcon.setAttribute('hidden', '');
        if (pauseIcon) pauseIcon.removeAttribute('hidden');
        if (muteBtn) muteBtn.removeAttribute('hidden');
        wrap.classList.add('kizen-tr__media-wrap--playing');
      } else {
        video.pause();
        if (playIcon) playIcon.removeAttribute('hidden');
        if (pauseIcon) pauseIcon.setAttribute('hidden', '');
        wrap.classList.remove('kizen-tr__media-wrap--playing');
      }
    }

    handleMuteToggle(event) {
      const btn = event.currentTarget;
      const wrap = btn.closest('.kizen-tr__media-wrap');
      if (!wrap) return;

      const video = wrap.querySelector('.kizen-tr__video');
      if (!video) return;

      const mutedIcon = btn.querySelector('.kizen-tr__icon-muted');
      const unmutedIcon = btn.querySelector('.kizen-tr__icon-unmuted');

      video.muted = !video.muted;
      btn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      if (mutedIcon) mutedIcon.toggleAttribute('hidden', !video.muted);
      if (unmutedIcon) unmutedIcon.toggleAttribute('hidden', video.muted);
    }

    /* ------ Video tab: See More progressive reveal ------ */

    getVideoPanel() {
      return this.panels.find((panel) => panel.dataset.panel === 'video');
    }

    initializeResponsiveCardVisibility() {
      const panel = this.getVideoPanel();
      if (!panel) return;

      const container = panel.querySelector('[data-card-container]');
      if (!container) return;

      const cards = Array.from(container.querySelectorAll('.kizen-tr__card'));
      const batchSize = this.getBatchSizeForPanel(panel);
      const visibleCount = cards.length <= this.reviewsPerPage
        ? cards.length
        : Math.min(cards.length, batchSize);

      cards.forEach((card, index) => {
        card.classList.toggle('kizen-tr__card--hidden', index >= visibleCount);
      });

      this.updateSeeMoreButton(panel);
    }

    handleResize() {
      const panel = this.getVideoPanel();
      if (panel) {
        const container = panel.querySelector('[data-card-container]');
        if (container) {
          const cards = Array.from(container.querySelectorAll('.kizen-tr__card'));
          const shownCards = cards.filter((card) => !card.classList.contains('kizen-tr__card--hidden')).length;
          const minimumVisible = Math.min(cards.length, this.getBatchSizeForPanel(panel));
          const targetVisibleCount = Math.max(shownCards, minimumVisible);

          cards.forEach((card, index) => {
            card.classList.toggle('kizen-tr__card--hidden', index >= targetVisibleCount);
          });

          this.updateSeeMoreButton(panel);
        }
      }

      this.refreshWrittenSlider();
    }

    getBatchSizeForPanel(panel) {
      return Math.max(1, this.getColumnCountForViewport() * this.rowsPerBatch);
    }

    getColumnCountForViewport() {
      if (window.matchMedia('(min-width: 990px)').matches) {
        return 4;
      }

      if (window.matchMedia('(min-width: 750px)').matches) {
        return 3;
      }

      return 2;
    }

    updateSeeMoreButton(panel) {
      const container = panel.querySelector('[data-card-container]');
      const button = panel.querySelector('[data-see-more]');

      if (!container || !button) return;

      const hasHiddenCards = container.querySelector('.kizen-tr__card--hidden');
      button.classList.toggle('kizen-tr__more-btn--hidden', !hasHiddenCards);
    }

    bindSeeMoreEvents() {
      this.seeMoreButtons.forEach((button) => {
        button.addEventListener('click', this.handleSeeMore.bind(this));
      });
    }

    handleSeeMore(event) {
      const button = event.currentTarget;
      const panel = button.closest('[role="tabpanel"]');
      if (!panel || panel.dataset.panel !== 'video') return;

      const container = panel.querySelector('[data-card-container]');
      if (!container) return;

      const hiddenCards = container.querySelectorAll('.kizen-tr__card--hidden');
      const batch = Array.from(hiddenCards).slice(0, this.getBatchSizeForPanel(panel));

      batch.forEach((card) => {
        card.classList.remove('kizen-tr__card--hidden');

        if (card.classList.contains('scroll-trigger')) {
          card.classList.remove('animate--slide-in');
          void card.offsetWidth;
          card.classList.add('animate--slide-in');
        }
      });

      this.updateSeeMoreButton(panel);
    }

    /* ------ Written tab: responsive Swiper ------ */

    initWrittenSlider() {
      const sliderRoot = this.querySelector('[data-written-slider]');
      const swiperEl = sliderRoot?.querySelector('[data-written-swiper]');

      if (!sliderRoot || !swiperEl || typeof Swiper === 'undefined') return;

      const prevBtn = sliderRoot.querySelector('[data-written-prev]');
      const nextBtn = sliderRoot.querySelector('[data-written-next]');

      this.writtenSwiper = new Swiper(swiperEl, {
        slidesPerView: 2,
        spaceBetween: 16,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
          990: {
            slidesPerView: 4,
            spaceBetween: 25,
          },
        },
        navigation:
          prevBtn && nextBtn
            ? {
                prevEl: prevBtn,
                nextEl: nextBtn,
              }
            : false,
        on: {
          init: () => this.updateWrittenSliderNav(),
          resize: () => this.updateWrittenSliderNav(),
          lock: () => this.updateWrittenSliderNav(),
          unlock: () => this.updateWrittenSliderNav(),
          slideChange: () => this.updateWrittenSliderNav(),
        },
      });
    }

    refreshWrittenSlider() {
      if (!this.writtenSwiper) return;

      requestAnimationFrame(() => {
        this.writtenSwiper.update();
        this.updateWrittenSliderNav();
      });
    }

    updateWrittenSliderNav() {
      const navWrap = this.querySelector('[data-written-nav]');
      if (!navWrap || !this.writtenSwiper) return;

      const isLocked = this.writtenSwiper.isLocked;
      navWrap.hidden = isLocked;

      const prevBtn = navWrap.querySelector('[data-written-prev]');
      const nextBtn = navWrap.querySelector('[data-written-next]');

      if (prevBtn) {
        prevBtn.classList.toggle('disabled', this.writtenSwiper.isBeginning);
        prevBtn.disabled = this.writtenSwiper.isBeginning;
      }

      if (nextBtn) {
        nextBtn.classList.toggle('disabled', this.writtenSwiper.isEnd);
        nextBtn.disabled = this.writtenSwiper.isEnd;
      }
    }

    destroyWrittenSlider() {
      if (this.writtenSwiper) {
        this.writtenSwiper.destroy(true, true);
        this.writtenSwiper = null;
      }
    }
  }

  customElements.define('testimonials-tabbed', TestimonialsTabbed);
}
